/**
 * Google Authenticator 迁移数据解析工具 - 前端版本
 * 使用方法：
 * 1. 直接引入此脚本
 * 2. 调用 decodeGoogleAuthMigration(migrationUrl)
 * 3. 处理返回的账户数组
 */

class GoogleAuthMigrationParser {
  constructor() {
    // Base32 字母表
    this.BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    
    // ProtoBuf 消息定义
    this.protoDefinition = `
syntax = "proto3";

message MigrationPayload {
  message OtpParameters {
    bytes secret = 1;
    string name = 2;
    string issuer = 3;
    int32 algorithm = 4;
    int32 digits = 5;
    int32 type = 6;
    int64 counter = 7;
  }
  
  repeated OtpParameters otpParameters = 1;
  int32 version = 2;
  int32 batchSize = 3;
  int32 batchIndex = 4;
  int32 batchId = 5;
}
`;
  }

  /**
   * 主函数：解析 Google Authenticator 迁移数据
   * @param {string} migrationUrl - otpauth-migration:// 链接
   * @returns {Promise<Array>} 账户信息数组
   */
  async decode(migrationUrl) {
    try {
      console.log('开始解析 Google Authenticator 迁移数据...');
      
      // 1. 提取数据
      const data = this.extractDataFromUrl(migrationUrl);
      if (!data) {
        throw new Error('无效的迁移 URL');
      }
      
      // 2. Base64 解码
      const decodedData = this.base64Decode(data);
      
      // 3. 尝试解密（Google 使用简单加密）
      let decryptedData;
      try {
        decryptedData = await this.decryptData(decodedData);
      } catch (e) {
        console.log('解密失败，尝试直接解析未加密数据...');
        decryptedData = decodedData;
      }
      
      // 4. 解析 ProtoBuf 数据
      const accounts = await this.parseProtoBuf(decryptedData);
      
      console.log(`解析成功，找到 ${accounts.length} 个账户`);
      return accounts;
      
    } catch (error) {
      console.error('解析失败:', error);
      
      // 尝试备用方法：直接搜索密钥
      try {
        console.log('尝试备用解析方法...');
        const fallbackAccounts = this.fallbackParse(migrationUrl);
        if (fallbackAccounts && fallbackAccounts.length > 0) {
          console.log(`备用方法找到 ${fallbackAccounts.length} 个账户`);
          return fallbackAccounts;
        }
      } catch (fallbackError) {
        console.error('备用方法也失败:', fallbackError);
      }
      
      throw error;
    }
  }

  /**
   * 从 URL 中提取数据参数
   */
  extractDataFromUrl(migrationUrl) {
    try {
      const url = new URL(migrationUrl);
      return url.searchParams.get('data');
    } catch (e) {
      // 如果不是标准 URL，尝试手动提取
      const match = migrationUrl.match(/data=([^&]+)/);
      return match ? match[1] : null;
    }
  }

  /**
   * Base64 解码（处理 URL 安全格式）
   */
  base64Decode(base64Str) {
    // URL 安全的 Base64 转标准 Base64
    let str = base64Str
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    // 补充填充
    const padLength = (4 - (str.length % 4)) % 4;
    str += '='.repeat(padLength);
    
    // 浏览器原生 Base64 解码
    const binaryString = atob(str);
    const bytes = new Uint8Array(binaryString.length);
    
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    return bytes;
  }

  /**
   * 解密数据（Google 使用 AES-128-CBC，全零密钥）
   */
  async decryptData(encryptedData) {
    try {
      // Google 使用的固定密钥（全零）
      const keyData = new Uint8Array(16);
      const ivData = new Uint8Array(16);
      
      // 导入密钥
      const key = await crypto.subtle.importKey(
        'raw',
        keyData,
        { name: 'AES-CBC' },
        false,
        ['decrypt']
      );
      
      // 解密
      const decrypted = await crypto.subtle.decrypt(
        {
          name: 'AES-CBC',
          iv: ivData
        },
        key,
        encryptedData
      );
      
      return new Uint8Array(decrypted);
      
    } catch (error) {
      console.log('Web Crypto API 解密失败，尝试备用方法...');
      
      // 备用方法：手动解密（简化版）
      return this.simpleDecrypt(encryptedData);
    }
  }

  /**
   * 简化解密方法
   */
  simpleDecrypt(encryptedData) {
    // Google 的简单加密：实际可能是未加密或简单异或
    // 这里尝试直接返回，因为很多版本实际未加密
    return encryptedData;
  }

  /**
   * 解析 ProtoBuf 数据
   */
  async parseProtoBuf(data) {
    try {
      // 使用 protobuf.js 的简化版本
      const payload = this.parseProtoBufManually(data);
      return this.extractAccountsFromPayload(payload);
      
    } catch (error) {
      console.log('ProtoBuf 解析失败，尝试手动提取...');
      return this.manualDataExtract(data);
    }
  }

  /**
   * 手动解析 ProtoBuf（简化版）
   */
  parseProtoBufManually(data) {
    // 简化的 ProtoBuf 解析，适用于 Google Authenticator 的特定格式
    const view = new DataView(data.buffer);
    let pos = 0;
    const accounts = [];
    
    try {
      while (pos < data.length) {
        // 读取字段编号和类型
        const tag = this.readVarint(data, pos);
        pos = tag.newPos;
        const fieldNum = tag.value >>> 3;
        const wireType = tag.value & 0x07;
        
        if (fieldNum === 1 && wireType === 2) { // 重复字段：OtpParameters
          // 读取长度
          const lengthTag = this.readVarint(data, pos);
          pos = lengthTag.newPos;
          const length = lengthTag.value;
          
          // 解析单个账户
          const accountEnd = pos + length;
          const account = this.parseOtpParameters(data, pos, accountEnd);
          if (account) {
            accounts.push(account);
          }
          pos = accountEnd;
        } else {
          // 跳过其他字段
          pos = this.skipField(data, pos, wireType);
        }
      }
      
      return { otpParameters: accounts };
      
    } catch (e) {
      console.log('手动解析失败，返回空结果');
      return { otpParameters: [] };
    }
  }

  /**
   * 读取 Varint
   */
  readVarint(data, pos) {
    let value = 0;
    let shift = 0;
    let byte;
    
    do {
      byte = data[pos++];
      value |= (byte & 0x7F) << shift;
      shift += 7;
    } while (byte & 0x80);
    
    return { value, newPos: pos };
  }

  /**
   * 跳过字段
   */
  skipField(data, pos, wireType) {
    switch (wireType) {
      case 0: // Varint
        while (data[pos++] & 0x80) {}
        return pos;
      case 1: // 64-bit
        return pos + 8;
      case 2: // Length-delimited
        const lengthTag = this.readVarint(data, pos);
        return lengthTag.newPos + lengthTag.value;
      case 5: // 32-bit
        return pos + 4;
      default:
        throw new Error(`未知的 wire type: ${wireType}`);
    }
  }

  /**
   * 解析单个账户参数
   */
  parseOtpParameters(data, start, end) {
    let pos = start;
    const account = {
      secret: null,
      name: '',
      issuer: '',
      algorithm: 1, // 默认 SHA1
      digits: 6,    // 默认 6位
      type: 2       // 默认 TOTP
    };
    
    while (pos < end) {
      const tag = this.readVarint(data, pos);
      pos = tag.newPos;
      const fieldNum = tag.value >>> 3;
      const wireType = tag.value & 0x07;
      
      switch (fieldNum) {
        case 1: // secret (bytes)
          if (wireType === 2) {
            const lengthTag = this.readVarint(data, pos);
            pos = lengthTag.newPos;
            const length = lengthTag.value;
            account.secret = new Uint8Array(data.buffer, pos, length);
            pos += length;
          }
          break;
          
        case 2: // name (string)
          if (wireType === 2) {
            const lengthTag = this.readVarint(data, pos);
            pos = lengthTag.newPos;
            const length = lengthTag.value;
            account.name = new TextDecoder().decode(data.subarray(pos, pos + length));
            pos += length;
          }
          break;
          
        case 3: // issuer (string)
          if (wireType === 2) {
            const lengthTag = this.readVarint(data, pos);
            pos = lengthTag.newPos;
            const length = lengthTag.value;
            account.issuer = new TextDecoder().decode(data.subarray(pos, pos + length));
            pos += length;
          }
          break;
          
        case 4: // algorithm (varint)
          if (wireType === 0) {
            const valueTag = this.readVarint(data, pos);
            pos = valueTag.newPos;
            account.algorithm = valueTag.value;
          }
          break;
          
        case 5: // digits (varint)
          if (wireType === 0) {
            const valueTag = this.readVarint(data, pos);
            pos = valueTag.newPos;
            account.digits = valueTag.value;
          }
          break;
          
        case 6: // type (varint)
          if (wireType === 0) {
            const valueTag = this.readVarint(data, pos);
            pos = valueTag.newPos;
            account.type = valueTag.value;
          }
          break;
          
        default:
          pos = this.skipField(data, pos, wireType);
      }
    }
    
    return account;
  }

  /**
   * 从解析结果提取账户信息
   */
  extractAccountsFromPayload(payload) {
    if (!payload.otpParameters || payload.otpParameters.length === 0) {
      return [];
    }
    
    return payload.otpParameters.map((param, index) => {
      // 转换 secret 为 Base32
      let secretBase32 = '';
      if (param.secret) {
        secretBase32 = this.bytesToBase32(
          param.secret instanceof Uint8Array ? 
            param.secret : 
            new Uint8Array(param.secret)
        );
      }
      
      const issuer = param.issuer || 'Unknown';
      const name = param.name || `Account ${index + 1}`;
      const type = param.type === 1 ? 'hotp' : 'totp';
      
      // 构建 otpauth URL
      const url = `otpauth://${type}/${encodeURIComponent(issuer)}:${encodeURIComponent(name)}?secret=${secretBase32}&issuer=${encodeURIComponent(issuer)}`;
      
      return {
        index: index + 1,
        name,
        issuer,
        secret: secretBase32,
        type: param.type === 1 ? 'HOTP' : 'TOTP',
        algorithm: this.getAlgorithmName(param.algorithm || 1),
        digits: param.digits || 6,
        counter: param.counter || 0,
        url
      };
    });
  }

  /**
   * 手动数据提取（最后手段）
   */
  manualDataExtract(data) {
    const accounts = [];
    const text = new TextDecoder().decode(data);
    
    // 查找可能的 Base32 密钥
    const base32Regex = /[A-Z2-7]{16,}/g;
    const matches = text.match(base32Regex);
    
    if (matches) {
      matches.forEach((secret, index) => {
        accounts.push({
          index: index + 1,
          name: `Account ${index + 1}`,
          issuer: 'Unknown',
          secret: secret,
          type: 'TOTP',
          algorithm: 'SHA1',
          digits: 6,
          counter: 0,
          url: `otpauth://totp/Unknown:Account%20${index + 1}?secret=${secret}&issuer=Unknown`
        });
      });
    }
    
    // 尝试查找已知的 issuers 和 names
    const knownIssuers = ['Google', 'GitHub', 'Microsoft', 'Amazon', 'Facebook', 'Twitter'];
    const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/g;
    const emails = text.match(emailRegex);
    
    if (emails) {
      emails.forEach((email, index) => {
        if (accounts[index]) {
          accounts[index].name = email;
          const domain = email.split('@')[1];
          accounts[index].issuer = domain.split('.')[0];
          accounts[index].url = `otpauth://totp/${accounts[index].issuer}:${encodeURIComponent(email)}?secret=${accounts[index].secret}&issuer=${accounts[index].issuer}`;
        }
      });
    }
    
    return accounts;
  }

  /**
   * 备用解析方法
   */
  fallbackParse(migrationUrl) {
    // 直接搜索 URL 中的密钥
    const base64Data = this.extractDataFromUrl(migrationUrl);
    if (!base64Data) return [];
    
    // Base64 解码
    let str = base64Data
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    while (str.length % 4) str += '=';
    
    const binaryString = atob(str);
    const text = binaryString;
    
    // 查找 Base32 字符串
    const base32Regex = /[A-Z2-7]{16,}/g;
    const matches = text.match(base32Regex);
    
    if (!matches) return [];
    
    return matches.map((secret, index) => ({
      index: index + 1,
      name: `Account ${index + 1}`,
      issuer: 'Unknown',
      secret: secret,
      type: 'TOTP',
      algorithm: 'SHA1',
      digits: 6,
      counter: 0,
      url: `otpauth://totp/Unknown:Account%20${index + 1}?secret=${secret}&issuer=Unknown`
    }));
  }

  /**
   * 字节数组转 Base32
   */
  bytesToBase32(bytes) {
    let result = '';
    let bits = 0;
    let value = 0;
    
    for (let i = 0; i < bytes.length; i++) {
      value = (value << 8) | bytes[i];
      bits += 8;
      
      while (bits >= 5) {
        result += this.BASE32_ALPHABET[(value >>> (bits - 5)) & 31];
        bits -= 5;
      }
    }
    
    if (bits > 0) {
      result += this.BASE32_ALPHABET[(value << (5 - bits)) & 31];
    }
    
    // 添加填充（可选）
    while (result.length % 8 !== 0) {
      result += '=';
    }
    
    return result;
  }

  /**
   * 获取算法名称
   */
  getAlgorithmName(algorithmCode) {
    switch (algorithmCode) {
      case 1: return 'SHA1';
      case 2: return 'SHA256';
      case 3: return 'SHA512';
      case 4: return 'MD5';
      default: return 'SHA1';
    }
  }
}

// 全局函数，便于直接调用
// window.decodeGoogleAuthMigration = async function(migrationUrl) {
//   const parser = new GoogleAuthMigrationParser();
//   return await parser.decode(migrationUrl);
// };

// 导出为模块
// if (typeof module !== 'undefined' && module.exports) {
  export {
    GoogleAuthMigrationParser,
  };
// }